// Helpers purs del pipeline de dades dels widgets, extrets de Dashboard.vue perquè
// els puguin compartir el dashboard i l'editor (preview en viu). Cap dependència de
// l'estat d'una instància: tot el que abans venia de refs de Dashboard entra per
// arguments (lang, mode/drillPath, etc.).

import { tConfig } from '../i18n/index.js';
import { debugLog } from './debug.js';

export const CUMULATIVE_KEY = '__cumulative__';
export const CUMULATIVE_SEP = ' • ';

// Paleta multi-sèrie per al mode "multi" (stack) quan el widget no en porta de pròpia.
export const MULTI_PALETTE = [
  '#0b5394', '#3689b7', '#5ab4d2', '#86cabd', '#b3d89d', '#d4a574',
  '#a47ec4', '#c46b8e', '#7ab4d4', '#6bc18e', '#e0a060', '#90a8c4'
];

export const FORMATTER_PRESETS = {
  millions: (val) => {
    if (val === undefined || val === null || isNaN(val)) return val;
    return (val / 1_000_000).toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + 'M';
  },
  thousands: (val) => {
    if (val === undefined || val === null || isNaN(val)) return val;
    return (val / 1_000).toLocaleString('es-ES', { maximumFractionDigits: 0 }) + 'K';
  },
  raw: (val) => val
};

/** Lectura segura d'una propietat niada per path "a.b.c". */
export function getNestedProperty(obj, path) {
  if (!path || !obj) return undefined;
  const keys = path.split('.');
  let value = obj;
  for (const key of keys) {
    if (value && typeof value === 'object') {
      value = value[key];
    } else {
      return undefined;
    }
  }
  return value;
}

/**
 * Valor formatat d'una cel·la de taula per a un path de camp. Gestiona agregades
 * FUNC(camp) (buit → "—"), imports en € i enunciats de domini. Funció pura.
 * @param {object} item fila de dades
 * @param {string} fieldPath camp/path (pot ser "SUM(vipCosts.costEuros)")
 * @param {object} domainLabels mapa key→text de domini
 * @param {boolean} euro formatar imports com €
 */
export function getTableCellValue(item, fieldPath, domainLabels, euro = false) {
  if (!fieldPath || !item) return 'N/A';
  const isMetric = /^(SUM|AVG|MIN|MAX|MEDIAN)\(/i.test(fieldPath);
  const value = getNestedProperty(item, fieldPath) ?? item[fieldPath];
  if (value === undefined || value === null) return isMetric ? '—' : 'N/A';
  if (typeof value === 'number') {
    if (euro && isMetric) {
      return Math.round(value).toLocaleString('es-ES', { maximumFractionDigits: 0, useGrouping: 'always' }) + ' €';
    }
    return value.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }
  if (domainLabels && domainLabels[value] != null) return domainLabels[value];
  return value;
}

/**
 * Resol un nom de mapping (ex. "indicators[indicatorId=...].valueDescLanguage") a la
 * clau real que retorna l'API (que pot tenir [],"," eliminats o substituits).
 * @param {string} mappingName nom declarat al config
 * @param {string[]} keys claus reals del primer element de dades
 * @return {string|null}
 */
export function resolveProp(mappingName, keys) {
  if (!mappingName || !keys || keys.length === 0) return null;
  const normalize = (s) => s.toLowerCase().replace(/[\[\]"= _.]/g, '');
  const targetNorm = normalize(mappingName);
  // 1. Coincidència exacta
  if (keys.includes(mappingName)) return mappingName;
  // 2. Coincidència exacta normalitzada (separadors diferents però mateix nom)
  let prop = keys.find(k => normalize(k) === targetNorm);
  if (prop) return prop;
  // 3. Inclusió amb límit de paraula (evita que "comarca" matcheji "comarcamap")
  prop = keys.find(k => {
    const kn = normalize(k);
    if (!kn.includes(targetNorm)) return false;
    const idx = kn.indexOf(targetNorm);
    const after = kn[idx + targetNorm.length];
    return after === undefined;
  });
  if (prop) return prop;
  // 4. Inclusió inversa (mappingName conté la clau de l'API tal qual)
  prop = keys.find(k => {
    const kn = normalize(k);
    return kn.length >= 3 && targetNorm.includes(kn);
  });
  if (prop) return prop;
  // 5. Fallback heurístic per a indicadors amb format complex
  return keys.find(k =>
    k.includes('valueDesc') ||
    k.includes('indicator') ||
    k.toLowerCase().includes('description')
  ) || null;
}

/**
 * Transforma un widget "cru" en el widget "efectiu" segons el mode de visualització
 * (drill/multi/cumulative) i el camí de drill. Funció pura: el mode i el camí entren
 * per arguments (abans venien de displayModes/drillStates de la instància).
 * @param {object} widget widget cru
 * @param {{mode?: string, drillPath?: Array}} ctx
 * @return {object} widget efectiu
 */
export function getEffectiveWidget(widget, { mode = 'drill', drillPath = [] } = {}) {
  const drilldown = widget.params?.drilldown;
  if (!Array.isArray(drilldown) || drilldown.length === 0) return widget;

  if (mode === 'multi') {
    const dim0 = drilldown[0];
    const dim1 = drilldown[1] || drilldown[0];
    const baseMapping = widget.params.mapping || {};
    const baseColors = widget.params.colors || [];
    const multiPalette = baseColors.length > 1 ? baseColors : MULTI_PALETTE;
    return {
      ...widget,
      params: {
        ...widget.params,
        dimension: drilldown.slice(0, 2),
        mapping: { ...baseMapping, category: dim0, group: dim1 },
        chart: { ...(widget.params.chart || {}), stacked: true },
        colors: multiPalette,
        legend: { ...(widget.params.legend || {}), show: true, position: widget.params.legend?.position || 'bottom' }
      }
    };
  }

  if (mode === 'cumulative') {
    const numLevels = Math.min(drillPath.length + 1, drilldown.length);
    const activeDims = drilldown.slice(0, numLevels);
    const baseMapping = widget.params.mapping || {};
    const { group: _g2, ...mappingNoGroup } = baseMapping;
    return {
      ...widget,
      params: {
        ...widget.params,
        dimension: activeDims,
        mapping: { ...mappingNoGroup, category: CUMULATIVE_KEY }
      }
    };
  }

  const levelIdx = Math.min(drillPath.length, drilldown.length - 1);
  const effectiveDimension = drilldown[levelIdx];
  const baseMapping = widget.params.mapping || {};
  const { group: _g, ...mappingNoGroup } = baseMapping;
  return {
    ...widget,
    params: {
      ...widget.params,
      dimension: effectiveDimension,
      mapping: { ...mappingNoGroup, category: effectiveDimension }
    }
  };
}

/**
 * Explota files amb camps multi-resposta en una fila per token. Per a cada `key` de
 * `fieldKeys`, una fila amb valor "|a|b|" es parteix en dues (una amb `a`, una amb `b`),
 * conservant la resta de camps i el valor; els tokens buits o duplicats dins d'una mateixa
 * fila s'ignoren (una fila sense cap token es manté tal qual, per conservar el bucket buit).
 * Diversos camps = explosió en cascada (producte cartesià). Funció pura.
 * @param {object[]} list files de dades
 * @param {string[]} fieldKeys claus reals (ja resoltes) dels camps multi-resposta
 * @param {string} sep separador de tokens
 * @return {object[]} llista explotada
 */
function explodeMultiValue(list, fieldKeys, sep) {
  let out = list;
  for (const key of fieldKeys) {
    const next = [];
    out.forEach(item => {
      const tokens = String(item[key] ?? '').split(sep).map(t => t.trim()).filter(Boolean);
      if (tokens.length === 0) { next.push(item); return; }
      const seen = new Set();
      tokens.forEach(tok => {
        if (seen.has(tok)) return; // no duplicar si un combo repeteix token
        seen.add(tok);
        next.push({ ...item, [key]: tok });
      });
    });
    out = next;
  }
  return out;
}

/**
 * Modela la resposta crua de l'API a la forma que consumeix cada tipus de widget
 * (sèries/labels/categories, node de dades per taules/mapes/pivots, escalars de KPI).
 * Funció pura: l'idioma entra per `ctx.lang`.
 * @param {object} rawData resposta de getDashboardStats
 * @param {object} widget widget efectiu
 * @param {{lang?: string}} ctx
 */
export function transformApiData(rawData, widget, { lang = 'ca_es' } = {}) {
  let list = Array.isArray(rawData) ? rawData : (rawData?.data || []);

  if (!Array.isArray(list) || list.length === 0) return { series: [], categories: [], labels: [] };

  // Mapping efectiu: a l'esquema canònic `mapping` només porta `value`; `category`/`group`
  // es deriven de `dimension` (1a dim → categoria, 2a → grup) quan no venen al config.
  // Clon local (no mutem widget.params, que és compartit). Amb configs antigues que SÍ
  // porten category/group és un no-op → invisible.
  const rawMapping = widget.params?.mapping || {};
  const dimsForMap = Array.isArray(widget.params?.dimension)
    ? widget.params.dimension.filter(Boolean)
    : (widget.params?.dimension ? [widget.params.dimension] : []);
  const isTableChart = widget.type === 'chart' && widget.params?.chart?.type === 'table';
  const mapping = { ...rawMapping };
  if ((mapping.category == null || mapping.category === '') && dimsForMap.length) mapping.category = dimsForMap[0];
  // El `group` NO es deriva per a taules: faria caure al branch de creuament (sèries) en
  // comptes del branch de taula ({data}). Les taules consumeixen la llista de dimensions.
  if (!isTableChart && (mapping.group == null || mapping.group === '') && dimsForMap.length > 1) mapping.group = dimsForMap[1];
  const valueKey = mapping.value || 'count';

  const dataKeys = list.length > 0 ? Object.keys(list[0]) : [];
  const categoryProp = resolveProp(mapping?.category, dataKeys);
  const groupProp = resolveProp(mapping?.group, dataKeys);

  debugLog('Widget:', widget.id, '| Keys:', dataKeys, '| CategoryProp:', categoryProp, '| GroupProp:', groupProp);

  const isCrossTable = widget.type === 'pivot' || widget.type === 'matrix-table' || widget.type === 'tree-table';

  // Multi-resposta en CREUAMENT: quan un camp conté diversos valors en una cadena
  // ("|advisor|speakercongreso|"), cada token ha de comptar al seu propi bucket dins del
  // creuament (pivot/matrix/stack), no com un valor combinat. Per a gràfics d'1 dimensió
  // ja ho fa el bloc d'agregació de sota (!group); aquí EXPLOTEM cada fila en una fila per
  // token de la/les dimensió/ns multi-valor, conservant la resta de camps i el count,
  // perquè la niació posterior bucketitzi cada token per separat.
  // Camps a partir: `multiValueFields` si es declara; si no, TOTES les dimensions del widget
  // (dimension[] + category + group). explodeMultiValue deixa intactes els camps sense
  // separador, així que partir una dimensió mono-valor és un no-op segur → amb només
  // `multiValueSeparator` ja funciona per a 1 eix, per als DOS, i per pivot/matrix/stack.
  // ⚠️ Els totals de fila/columna superaran la mida del segment (un doc amb N tokens compta
  // a N buckets): és la semàntica correcta de multi-resposta.
  const crossSep = widget.params?.multiValueSeparator;
  if (crossSep && list.length > 0 && (mapping?.group || isCrossTable)) {
    const dimsDeclared = Array.isArray(widget.params?.dimension)
      ? widget.params.dimension
      : (widget.params?.dimension ? [widget.params.dimension] : []);
    const declaredFields = Array.isArray(widget.params?.multiValueFields) && widget.params.multiValueFields.length
      ? widget.params.multiValueFields
      : [...dimsDeclared, mapping?.category, mapping?.group];
    const fieldKeys = [...new Set(declaredFields.filter(Boolean).map(name => resolveProp(name, dataKeys) || name).filter(Boolean))];
    if (fieldKeys.length) {
      list = explodeMultiValue(list, fieldKeys, crossSep);
    }
  }

  // Crear categoryLabel a partir de la propietat detectada
  if (list.length > 0 && categoryProp) {
    list = list.map(item => ({
      ...item,
      categoryLabel: item[categoryProp] || 'N/A'
    }));
  }

  // Dimensions multi-resposta: el backend retorna el valor com una cadena combinada
  // (p.ex. "|en|es|"). Amb multiValueSeparator partim cada valor en els seus tokens i
  // sumem el count a cadascun. Només per a gràfics simples (sense agrupació).
  const multiSep = widget.params?.multiValueSeparator;
  if (multiSep && !mapping?.group && !isCrossTable && list.length > 0) {
    const agg = new Map();
    list.forEach(item => {
      const raw = String(item.categoryLabel ?? item[mapping?.category] ?? '');
      const tokens = raw.split(multiSep).map(t => t.trim()).filter(Boolean);
      const value = Number(item[valueKey]) || 0;
      const seen = new Set();
      (tokens.length ? tokens : ['N/A']).forEach(tok => {
        if (seen.has(tok)) return; // no comptar dos cops si un combo repeteix token
        seen.add(tok);
        agg.set(tok, (agg.get(tok) || 0) + value);
      });
    });
    list = Array.from(agg, ([cat, value]) => ({ categoryLabel: cat, [valueKey]: value }));
  }

  // Dimensions de data (epoch ms): amb dateBucket='month'|'year' agrupem cada valor en
  // el seu tram i sumem el count. Només per a gràfics simples (sense agrupació).
  const dateBucket = widget.params?.dateBucket;
  if (dateBucket && !mapping?.group && list.length > 0) {
    const agg = new Map();
    list.forEach(item => {
      const ms = Number(item.categoryLabel ?? item[mapping?.category]);
      const value = Number(item[valueKey]) || 0;
      let label = 'N/A';
      if (!isNaN(ms) && ms > 0) {
        const d = new Date(ms);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        label = dateBucket === 'year' ? String(y) : `${y}-${m}`;
      }
      agg.set(label, (agg.get(label) || 0) + value);
    });
    list = Array.from(agg, ([cat, value]) => ({ categoryLabel: cat, [valueKey]: value }));
  }

  if (widget.type === 'kpi') {
    const total = list.reduce((sum, item) => sum + (Number(item[valueKey]) || 0), 0);
    // KPI de ràtio creuat: % d'un valor (kpiRatioTarget) dins d'un àmbit (kpiRatioScope)
    // de dues dimensions creuades. dimension = [scopeDim, targetDim].
    const ratioScope = widget.params?.kpiRatioScope;
    const ratioTarget = widget.params?.kpiRatioTarget;
    if (ratioScope !== undefined && ratioTarget !== undefined && Array.isArray(widget.params?.dimension)) {
      const [scopeDim, targetDim] = widget.params.dimension;
      const scopeKey = resolveProp(scopeDim, dataKeys) || scopeDim;
      const targetKey = resolveProp(targetDim, dataKeys) || targetDim;
      let denom = 0, num = 0;
      list.forEach(item => {
        if (String(item[scopeKey]) !== String(ratioScope)) return;
        const c = Number(item[valueKey]) || 0;
        denom += c;
        if (String(item[targetKey]) === String(ratioTarget)) num += c;
      });
      const pct = denom > 0 ? (num / denom) * 100 : 0;
      return { [widget.params.valueKey || 'total']: pct.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + '%' };
    }

    // KPI de comptatge d'un o més valors concrets de la dimensió (número absolut).
    const countOf = widget.params?.kpiCountOf;
    if (countOf !== undefined && countOf !== null) {
      const catProp = categoryProp || mapping?.category;
      const wanted = (Array.isArray(countOf) ? countOf : [countOf]).map(String);
      const matched = list.reduce((sum, item) => {
        const cat = item.categoryLabel ?? (catProp ? item[catProp] : undefined);
        return sum + (wanted.includes(String(cat)) ? (Number(item[valueKey]) || 0) : 0);
      }, 0);
      return { [widget.params.valueKey || 'total']: matched };
    }

    // KPI de comptatge de tots els valors EXCEPTE els indicats.
    const countNotOf = widget.params?.kpiCountNotOf;
    if (countNotOf !== undefined && countNotOf !== null) {
      const catProp = categoryProp || mapping?.category;
      const excluded = (Array.isArray(countNotOf) ? countNotOf : [countNotOf]).map(String);
      const matched = list.reduce((sum, item) => {
        const cat = item.categoryLabel ?? (catProp ? item[catProp] : undefined);
        return sum + (!excluded.includes(String(cat)) ? (Number(item[valueKey]) || 0) : 0);
      }, 0);
      return { [widget.params.valueKey || 'total']: matched };
    }

    // KPI de percentatge. Retornem el NUMERADOR; el % es calcula al Dashboard.
    if (widget.params?.kpiPercentAny) {
      // numerador = registres amb qualsevol valor. Sumem otherDocCount (buckets del terms no
      // retornats, exacte) perquè en camps d'alta cardinalitat el terms no capi el compte a ~1000.
      return { [widget.params.valueKey || 'total']: total + (Number(rawData?.otherDocCount) || 0) };
    }
    const target = widget.params?.kpiPercentOf;
    if (target !== undefined && target !== null) {
      const catProp = categoryProp || mapping?.category;
      const matched = list.reduce((sum, item) => {
        const cat = item.categoryLabel ?? (catProp ? item[catProp] : undefined);
        return sum + (String(cat) === String(target) ? (Number(item[valueKey]) || 0) : 0);
      }, 0);
      return { [widget.params.valueKey || 'total']: matched };
    }
    return { [widget.params.valueKey || 'total']: total };
  }

  // Gràfic de PRESÈNCIA (pie/donut "Amb info / Sense info" sobre un camp de text).
  if (widget.params?.presence) {
    const catProp = categoryProp || mapping?.category;
    // "Amb info" = docs amb valor no buit. Als camps d'alta cardinalitat (URLs de web/xarxes)
    // el terms es topa (size) i deixa buckets fora → hi sumem otherDocCount (sum_other_doc_count
    // del terms, exacte); els buckets no retornats són tots valors no buits (el terms no porta missing).
    const conInfo = list.reduce((sum, item) => {
      const cat = item.categoryLabel ?? (catProp ? item[catProp] : undefined);
      const has = cat !== undefined && cat !== null && String(cat).trim() !== '' && String(cat) !== 'N/A';
      return sum + (has ? (Number(item[valueKey]) || 0) : 0);
    }, 0) + (Number(rawData?.otherDocCount) || 0);
    const pl = widget.params.presenceLabels || {};
    const yes = tConfig(pl.yes, lang) || 'Con info';
    const no = tConfig(pl.no, lang) || 'Sin info';
    return { presence: true, conInfo, labels: [yes, no] };
  }

  if (widget.type === 'pivot' || widget.type === 'matrix-table' || widget.type === 'tree-table') {
    const valueProp = resolveProp(mapping?.value, dataKeys) || mapping?.value;
    // Totes les dimensions declarades → clau amb el nom de config (perquè el pivot pugui
    // niar per qualsevol nivell de la llista, no només category/group).
    const dimList = Array.isArray(widget.params?.dimension)
      ? widget.params.dimension
      : (widget.params?.dimension ? [widget.params.dimension] : []);
    const dimResolved = dimList
      .filter(Boolean)
      .map(name => ({ name, prop: resolveProp(name, dataKeys) }));
    const rows = list.map(item => {
      const row = { ...item };
      dimResolved.forEach(({ name, prop }) => {
        if (prop && prop !== name) row[name] = item[prop];
      });
      if (mapping?.category && categoryProp && categoryProp !== mapping.category) {
        row[mapping.category] = item[categoryProp];
      }
      if (mapping?.group && groupProp && groupProp !== mapping.group) {
        row[mapping.group] = item[groupProp];
      }
      if (mapping?.value && valueProp && valueProp !== mapping.value) {
        row[mapping.value] = item[valueProp];
      }
      return row;
    });
    return { data: rows };
  }

  if (mapping?.category && mapping?.group && !isTableChart) {
    const categoryKey = categoryProp || mapping.category;
    const groupKey = groupProp || mapping.group;

    let categories = [...new Set(list.map(item => String(item[categoryKey] ?? 'N/A')))];
    const groups = [...new Set(list.map(item => String(item[groupKey] ?? 'N/A')))].sort();

    const sortBy = widget.params?.sortBy || 'category';
    if (sortBy === 'total') {
      const totals = {};
      list.forEach(item => {
        const cat = String(item[categoryKey] ?? 'N/A');
        totals[cat] = (totals[cat] || 0) + (Number(item[valueKey]) || 0);
      });
      categories.sort((a, b) => (totals[b] || 0) - (totals[a] || 0));
    } else {
      categories.sort((a, b) => {
        const aNum = Number(a);
        const bNum = Number(b);
        if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
        return String(a).localeCompare(String(b), 'ca-ES');
      });
    }

    const series = groups.map(groupName => ({
      name: groupName,
      data: categories.map(catName => {
        const match = list.find(item => String(item[categoryKey]) === catName && String(item[groupKey]) === groupName);
        return match ? Number(match[valueKey]) || 0 : 0;
      })
    }));

    const displayCategories = categories.map(cat => {
      const num = Number(cat);
      if (!isNaN(num) && cat.includes('.')) return Math.floor(num).toString();
      return cat;
    });

    return { series, categories: displayCategories };
  }

  if (widget.params?.chart?.type === 'table') {
    const valueProp = resolveProp(mapping?.value, dataKeys) || mapping?.value;
    // Columna % opcional (gated): només si el config declara una columna field:'percent'.
    // És el share sobre el total de la columna de valor (suma dels comptes) → suma 100%.
    const wantsPercent = Array.isArray(widget.params?.columns)
      && widget.params.columns.some(c => c.field === 'percent');
    const percentTotal = wantsPercent
      ? list.reduce((sum, it) => sum + (Number(it[valueProp] ?? it[valueKey]) || 0), 0)
      : 0;
    const rows = list.map(item => {
      const row = { ...item };
      if (mapping?.category && categoryProp && categoryProp !== mapping.category) {
        row[mapping.category] = item[categoryProp];
      }
      if (mapping?.value && valueProp && valueProp !== mapping.value) {
        row[mapping.value] = item[valueProp];
      }
      if (wantsPercent) {
        const v = Number(item[valueProp] ?? item[valueKey]) || 0;
        row.percent = percentTotal > 0 ? Math.round((v / percentTotal) * 100) + '%' : '0%';
      }
      return row;
    });
    return { data: rows };
  }

  if (widget.type === 'map' || widget.type === 'world-map') {
    const dataKeyField = widget.params?.dataKeyField || mapping.category;
    const valueField = widget.params?.valueField || mapping.value || 'count';
    const mapKeyProp = resolveProp(dataKeyField, dataKeys);
    const valueProp = resolveProp(valueField, dataKeys) || valueField;
    const rows = list.map(item => {
      const row = { ...item };
      if (dataKeyField && mapKeyProp && mapKeyProp !== dataKeyField) {
        row[dataKeyField] = item[mapKeyProp];
      }
      if (valueField && valueProp && valueProp !== valueField) {
        row[valueField] = item[valueProp];
      }
      return row;
    });
    return { data: rows };
  }

  const categoryKey = mapping?.category || mapping?.label || 'categoryLabel' || rawData?.currentDimensionField || 'marca';

  const dataPoints = list.map(item => {
    let category = item.categoryLabel;
    if (!category) {
      const nestedValue = getNestedProperty(item, categoryKey);
      category = nestedValue || item[categoryKey] || 'N/A';
    }
    const value = Number(item[valueKey]) || 0;
    return { category, value, item };
  });

  const sortBy = widget.params?.sortBy || 'value';
  if (sortBy === 'value') {
    dataPoints.sort((a, b) => b.value - a.value);
  } else {
    dataPoints.sort((a, b) => {
      const aNum = Number(a.category);
      const bNum = Number(b.category);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      return String(a.category).localeCompare(String(b.category), 'ca-ES');
    });
  }

  const labels = dataPoints.map(p => {
    const cat = String(p.category);
    const num = Number(cat);
    if (!isNaN(num) && cat.includes('.')) {
      return Math.floor(num).toString();
    }
    return cat;
  });
  const values = dataPoints.map(p => p.value);
  const items = dataPoints.map(p => p.item);

  if (widget.params?.chart?.type === 'donut' || widget.params?.chart?.type === 'pie') {
    return { series: values, labels, items };
  }

  return {
    items,
    series: [{ name: tConfig(widget.params?.title?.text || widget.params?.title, lang) || 'Total', data: values }],
    categories: labels
  };
}
